/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {MenuList} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {CellMenuItem} from '../components/grid/CellMenuItem';
import ResponsiveMenu from '../components/menus/ResponsiveMenu';
import Tooltip from '../components/tooltip/Tooltip';
import {FilterDrawerFavoriteFilledIcon} from '../icons/current/FilterDrawerFavoriteFilledIcon';
import {FilterDrawerFavoriteIcon} from '../icons/current/FilterDrawerFavoriteIcon';
import {MapEditIcon} from '../icons/current/MapEditIcon';
import {MaterialDeleteIcon} from '../icons/current/MaterialDeleteIcon';
import {MaterialMoreVertIcon} from '../icons/current/MaterialMoreVertIcon';
import {TextEditIcon} from '../icons/current/TextEditIcon';
import {ThunderIcon} from '../icons/current/ThunderIcon';
import Render from '../render/Render';
import {dlsColor, dlsZIndex} from '../theme/theme';
import {IconSize} from '../types/types';
import FilterEmptyState from './FilterEmptyState';

export interface FilterFavoriteListItem {
  dirty?: boolean;
  name: string;
  selected?: boolean;
}

export interface FilterFavoritesLabels {
  delete: string;
  dirtyMessage: string;
  dirtyPrefix: string;
  edit: string;
  emptyDescriptionPrefix: string;
  emptyDescriptionSuffix: string;
  emptyTitle: string;
  rename: string;
  reset: string;
  saveChanges: string;
}

export interface FilterFavoritesProps {
  items: FilterFavoriteListItem[];
  labels: FilterFavoritesLabels;
  onDelete: (name: string) => void;
  onEdit: (name: string) => void;
  onRename: (name: string) => void;
  onReset: (name: string) => void;
  onSaveChanges: (name: string) => void;
  onSelect: (name: string) => void;
  // leaves Edit out of each favorite's options menu ...
  hideEdit?: boolean;
}

// the options menu opens over the bottom of its three dots' button (by 18px,
// as designed), its right edge on theirs - so it stays within the favorites
// list, rather than running past it ...
const MENU_BUTTON_SIZE      = 44;
const MENU_BUTTON_OVERLAP   = 18;
const MENU_ANCHOR_ORIGIN    = {vertical: MENU_BUTTON_SIZE - MENU_BUTTON_OVERLAP, horizontal: 'right'} as const;
const MENU_TRANSFORM_ORIGIN = {vertical: 'top', horizontal: 'right'} as const;

const useStyles = makeStyles(() => ({
  root : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    width           : '100%'
  },
  // the favorites list's own spacing - the empty state has its own ...
  listRoot : {
    gap     : 24,
    padding : '24px 16px'
  },
  // only as wide as its text ...
  dirtyNotice : {
    alignItems      : 'center',
    alignSelf       : 'flex-start',
    backgroundColor : dlsColor('grey6'),
    borderRadius    : 8,
    boxSizing       : 'border-box',
    color           : dlsColor('grey1'),
    display         : 'flex',
    fontFamily      : 'Roboto !important',
    fontSize        : 12,
    fontWeight      : 400,
    gap             : 4,
    lineHeight      : '16px',
    minHeight       : 32,
    padding         : 8
  },
  dirtyPrefix : {
    fontWeight : 700
  },
  // only as tall as its rows - a single favorite leaves no space below it.
  // The empty state keeps its own height ...
  panel : {
    alignItems      : 'center',
    alignSelf       : 'stretch',
    backgroundColor : dlsColor('grey13'),
    borderRadius    : 12,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    gap             : 8,
    padding         : 16
  },
  row : {
    alignItems : 'center',
    alignSelf  : 'stretch',
    display    : 'flex',
    gap        : 8,
    height     : 44,
    minHeight  : 44
  },
  rowCard : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('white')}`,
    borderRadius    : 8,
    boxSizing       : 'border-box',
    color           : dlsColor('grey2'),
    cursor          : 'pointer',
    display         : 'flex',
    flex            : 1,
    gap             : 8,
    height          : 32,
    minWidth        : 0,
    padding         : 8
  },
  rowCardSelected : {
    backgroundColor : dlsColor('paleBlue'),
    borderColor     : dlsColor('pastelTeal')
  },
  rowButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    color           : dlsColor('grey2'),
    cursor          : 'pointer',
    display         : 'flex',
    flex            : 1,
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    gap             : 8,
    height          : 32,
    lineHeight      : '20px',
    minWidth        : 0,
    padding         : 0,
    textAlign       : 'left',
    '&:focus'       : {
      outline : 'none'
    },
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('skyBlue')}`,
      outlineOffset : 2
    }
  },
  rowName : {
    display      : 'block',
    flex         : 1,
    minWidth     : 0,
    overflow     : 'hidden',
    position     : 'relative',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap'
  },
  inlineActions : {
    alignItems : 'center',
    display    : 'flex',
    flexShrink : 0,
    gap        : 8
  },
  inlineAction : {
    backgroundColor : 'transparent',
    border          : 0,
    color           : dlsColor('darkestBlue'),
    cursor          : 'pointer',
    fontFamily      : 'Inter',
    fontSize        : 13,
    fontWeight      : 600,
    height          : 32,
    letterSpacing   : 0.5,
    lineHeight      : '16px',
    padding         : '0 16px',
    whiteSpace      : 'nowrap'
  },
  menuButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    borderRadius    : 8,
    boxSizing       : 'border-box',
    cursor          : 'pointer',
    display         : 'flex',
    height          : MENU_BUTTON_SIZE,
    justifyContent  : 'center',
    padding         : 8,
    width           : MENU_BUTTON_SIZE,
    '&:focus'       : {
      outline : 'none'
    },
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('skyBlue')}`,
      outlineOffset : 2
    }
  },
  // the options menu opens above the filter drawer it sits in (the drawer
  // clears the selected filters tier by one, at token + 1) ...
  menuOverlay : {
    zIndex : `${dlsZIndex('trackingFilterSelectedFiltersConfirmation') + 2} !important` as any
  },
  menuPaper : {
    zIndex             : `${dlsZIndex('trackingFilterSelectedFiltersConfirmation') + 2} !important` as any,
    '& .MuiPaper-root' : {
      boxSizing    : 'border-box',
      width        : 128,
      border       : `1px solid ${dlsColor('grey5')}`,
      borderRadius : 12,
      boxShadow    : 'none',
      filter       : 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
      overflow     : 'hidden'
    },
    '& .MuiList-root' : {
      padding : 0
    },
    // each item's own content box adds 8px, for the design's 16px inset ...
    '& .MuiMenuItem-root' : {
      boxSizing  : 'border-box',
      height     : 44,
      padding    : '8px',
      width      : '100%',
      fontFamily : 'Inter',
      fontWeight : 500,
      fontSize   : 14,
      lineHeight : '20px',
      color      : dlsColor('grey1')
    }
  },
  mobileMenuPaper : {
    borderRadius : '12px 12px 0 0',
    width        : 128
  },
  emptyRoot : {
    height  : 196,
    padding : '24px 16px'
  },
  emptyPanel : {
    height : 148
  },
  emptyText : {
    gap    : 16,
    height : 84
  },
  emptyTitle : {
    fontSize   : 20,
    fontWeight : 700,
    lineHeight : '22px'
  },
  emptyDescription : {
    alignItems     : 'center',
    display        : 'flex',
    flexWrap       : 'wrap',
    fontSize       : 20,
    fontWeight     : 400,
    justifyContent : 'center',
    lineHeight     : '22px',
    maxWidth       : '100%',
    overflow       : 'visible',
    whiteSpace     : 'normal'
  },
  emptyInlineIcon : {
    display   : 'inline-flex',
    height    : 24,
    margin    : '0 2px',
    transform : 'translateY(5px)',
    width     : 24
  }
}));

/**
 * Displays saved favorite filters inside a filter drawer category.
 *
 * ```typescript
 * import FilterFavorites from '@ion/dls/src/filters/FilterFavorites';
 *
 * ...
 *
 * <FilterFavorites items={favorites}
 *   labels={labels}
 *   onSelect={selectFavorite} />
 * ```
 */
export const FilterFavorites = ({
  items,
  labels,
  onDelete,
  onEdit,
  onRename,
  onReset,
  onSaveChanges,
  onSelect,
  hideEdit = false
}: FilterFavoritesProps) => {
  const classes                         = useStyles();
  const anchorRef                       = useRef<HTMLButtonElement | null>(null);
  const [menuFavorite, setMenuFavorite] = useState<string | null>(null);
  const hasDirtyFavorite                = useMemo(() => items.some(item => item.dirty), [items]);

  const closeMenu = useCallback(() => {
    setMenuFavorite(null);
  }, []);

  const openMenu = useCallback((event: MouseEvent<HTMLButtonElement>, name: string) => {
    anchorRef.current = event.currentTarget;
    setMenuFavorite(name);
  }, []);

  const handleMenuAction = useCallback((action: (name: string) => void) => {
    if (menuFavorite) {
      action(menuFavorite);
      closeMenu();
    }
  }, [closeMenu, menuFavorite]);

  return (
    <div className={`${classes.root} ${items.length ? classes.listRoot : ''}`}>
      <Render.If condition={hasDirtyFavorite}>
        <div className={classes.dirtyNotice}>
          <ThunderIcon color={dlsColor('grey1')} size={IconSize.small} />
          <span className={classes.dirtyPrefix}>{labels.dirtyPrefix}</span>
          <span>{labels.dirtyMessage}</span>
        </div>
      </Render.If>
      <Render.If condition={!items.length}>
        <FilterEmptyState title={labels.emptyTitle}
          description={(
            <>
              {labels.emptyDescriptionPrefix}
              <span className={classes.emptyInlineIcon}>
                <FilterDrawerFavoriteIcon color={dlsColor('grey2')} size={IconSize.medium} />
              </span>
              {labels.emptyDescriptionSuffix}
            </>
          )}
          icon={<FilterDrawerFavoriteFilledIcon color={dlsColor('grey1')} size={IconSize.medium} />}
          classNames={{
            description : classes.emptyDescription,
            panel       : classes.emptyPanel,
            root        : classes.emptyRoot,
            text        : classes.emptyText,
            title       : classes.emptyTitle
          }} />
      </Render.If>
      <Render.If condition={!!items.length}>
        <div className={classes.panel}>
          {items.map(item => (
            <div key={item.name} className={classes.row}>
              <div className={`${classes.rowCard} ${item.selected ? classes.rowCardSelected : ''}`}>
                <button type='button'
                  className={classes.rowButton}
                  onClick={function() {
                    onSelect(item.name);
                  }}>
                  <FilterDrawerFavoriteFilledIcon color={dlsColor('red')} size={IconSize.small} />
                  <Tooltip arrow title={<span>{item.name}</span>}>
                    <span className={classes.rowName}>{item.name}</span>
                  </Tooltip>
                </button>
                <Render.If condition={!!item.dirty}>
                  <span className={classes.inlineActions}>
                    <button type='button'
                      className={classes.inlineAction}
                      onClick={function() {
                        onSaveChanges(item.name);
                      }}>
                      {labels.saveChanges}
                    </button>
                    <button type='button'
                      className={classes.inlineAction}
                      onClick={function() {
                        onReset(item.name);
                      }}>
                      {labels.reset}
                    </button>
                  </span>
                </Render.If>
              </div>
              <button type='button'
                className={classes.menuButton}
                aria-label={labels.edit}
                onClick={function(event) {
                  openMenu(event, item.name);
                }}>
                <MaterialMoreVertIcon color={dlsColor('grey3')} size={IconSize.medium} />
              </button>
            </div>
          ))}
        </div>
      </Render.If>
      <ResponsiveMenu open={!!menuFavorite}
        anchorEl={anchorRef.current}
        menuClasses={{
          paper : classes.mobileMenuPaper,
          root  : classes.menuOverlay
        }}
        popoverClasses={{root: classes.menuPaper}}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
        onClose={closeMenu}>
        <MenuList autoFocus>
          <CellMenuItem icon={<TextEditIcon color={dlsColor('grey1')} size={IconSize.medium} />}
            label={labels.rename}
            onClick={function() {
              handleMenuAction(onRename);
            }} />
          {!hideEdit && (
            <CellMenuItem icon={<MapEditIcon color={dlsColor('grey1')} size={IconSize.medium} />}
              label={labels.edit}
              onClick={function() {
                handleMenuAction(onEdit);
              }} />
          )}
          <CellMenuItem icon={<MaterialDeleteIcon color={dlsColor('grey1')} size={IconSize.medium} />}
            label={labels.delete}
            onClick={function() {
              handleMenuAction(onDelete);
            }} />
        </MenuList>
      </ResponsiveMenu>
    </div>
  );
};

export default memo(FilterFavorites);
